import { LoadingButton } from "@mui/lab";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { GlobalContext } from "../../../GlobalContext";
import { Context } from "../../table/overtime/Context";
import { applyingOvertime } from "../../../utils/Overtime";
import moment from "moment";
import { trackGaClick } from "../../../utils/analytics";

const PartialText = ({ row }) => {
	const { t } = useTranslation("overtime.form.partial");

	const { setRows } = useContext(Context);

	const [partial, setPartial] = useState(row.partial_amount);

	return (
		<>
			<TextField
				label={t("partial.rows.partial.title")}
				placeholder={t("partial.rows.partial.placeholder")}
				value={partial}
				error={!/^\d{1,2}:[0-5]\d$/.test(partial)}
				InputProps={{
					endAdornment: (
						<InputAdornment sx={{ width: "100%", mr: -0.75 }} position="end">
							{`/ ${row.amount}`}
							<WatchLaterIcon />
						</InputAdornment>
					),
				}}
				onChange={(event) => {
					setPartial(event.target.value);
				}}
				onBlur={(event) => {
					setRows((prev) =>
						prev.map((r) => (r.id === row.id ? { ...r, partial_amount: event.target.value } : r)),
					);
				}}
				variant="outlined"
				size="small"
				sx={{
					width: "160px",
				}}
			/>
		</>
	);
};

const Partial = ({ open, setOpen, data, checked }) => {
	const { t } = useTranslation("overtime.form.partial");

	const {
		//apiRef,
		//rows,
		setRows,
		reloading,
		comments,
		loadingComments,
	} = useContext(Context);

	const { handlerAlert } = useContext(GlobalContext);

	const [amount, setAmount] = useState("00:00");
	const [overtimes, setOvertimes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [employeeComments, setEmployeeComments] = useState({});
	const [employeeNotes, setEmployeeNotes] = useState({});
	const [openCommentDialog, setOpenCommentDialog] = useState(false);
	const [openNoteDialog, setOpenNoteDialog] = useState(false);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

	const overtimeRequiresComment = JSON.parse(sessionStorage.getItem("privileges"))?.overtime?.overtime_requires_coment || false;

	const closing = () => {
		setOpen(false);
		setAmount("00:00");
		setLoading(false);
		setEmployeeComments({});
		setEmployeeNotes({});
		setOpenCommentDialog(false);
		setOpenNoteDialog(false);
		setSelectedEmployeeId(null);
	};

	useEffect(() => {
		if (open) {
			setOvertimes(data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending"));
		}
	}, [checked, data, open]);

	return (
		<>
			<Dialog
				fullWidth
				open={open}
				onClose={() => {
					closing();
				}}
			>
				<DialogTitle>
					<Typography variant="body3">{t("partial.title")}</Typography>
				</DialogTitle>

				{data.filter((d) => checked.includes(d.id)).some((d) => d.status !== "pending") && (
					<DialogContent
						sx={{
							backgroundColor: (theme) => theme.palette.background.default,
							borderBottom: "none",
						}}
						dividers
					>
						<Grid container spacing={2}>
							<Grid
								item
								xs={12}
								sx={{
									height: "100%",
								}}
							>
								<Alert
									severity="warning"
									sx={{
										width: "100%",
										opacity: 1,
									}}
									variant="filled"
								>
									<AlertTitle>{t("partial.alerts.already_managed.title")}</AlertTitle>

									{t("partial.alerts.already_managed.message")}
								</Alert>
							</Grid>
						</Grid>
					</DialogContent>
				)}

				<DialogContent
					sx={{
						backgroundColor: (theme) => theme.palette.background.default,
						borderBottom: "none",
						borderRadius: "0px",
						height: "100%",
					}}
					dividers
				>
					<Grid sx={{ mt: 0.25 }} container spacing={2}>
						<Grid item xs={9}>
							<Typography variant="body3">{t("partial.message1")}</Typography>
						</Grid>

						<Grid item xs={3}>
							<TextField
								sx={{ mt: -0.5, mb: 1.5 }}
								label={t("partial.rows.partial.title")}
								placeholder={t("partial.rows.partial.placeholder")}
								fullWidth
								size="small"
								value={amount}
								error={!/^\d{1,2}:[0-5]\d$/.test(amount)}
								InputProps={{
									endAdornment: (
										<InputAdornment sx={{ mr: -0.5 }} position="end">
											<WatchLaterIcon />
										</InputAdornment>
									),
								}}
								onChange={(event) => {
									//.replace(/^0+/, "")
									let nrows = [];
									setAmount(event.target.value);
									setRows((prev) => {
										nrows = prev.map((row) => {
											if (checked.includes(row.id) && row.status === "pending") {
												return {
													...row,
													partial_amount:
														moment.duration(event.target.value).asSeconds() >=
														moment.duration(row.amount).asSeconds()
															? row.amount
															: event.target.value,
												};
											} else {
												return row;
											}
										});

										return nrows;
									});

									setOvertimes(
										nrows.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending"),
									);
								}}
							/>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogContent dividers>
					<Typography variant="body3">
						{t("partial.message2")}

						{overtimes.length !== 0 && (
							<List>
								{overtimes.map((row, index) => (
									<ListItem
										sx={{ mt: 2 }}
										key={new Date().getTime() + index}
										secondaryAction={
											<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
												<PartialText row={row} checked={checked} setOvertimes={setOvertimes} />
												{overtimes.length > 1 && (
													<>
														<IconButton
															size="small"
														onClick={() => {
															setSelectedEmployeeId(row.id);
															setOpenCommentDialog(true);
															}}
														>
															<ModeCommentIcon />
														</IconButton>
														<IconButton
															size="small"
														onClick={() => {
															setSelectedEmployeeId(row.id);
															setOpenNoteDialog(true);
															}}
														>
															<StickyNote2Icon />
														</IconButton>
													</>
												)}
											</Box>
										}
									>
										<ListItemText
											primary={
												<Box
													sx={{
														textOverflow: "ellipsis",
														overflow: "hidden",
														whiteSpace: "nowrap",
														width: "370px",
													}}
												>
													{`${row.date} - ${row.full_name}`}
												</Box>
											}
										/>
									</ListItem>
								))}
							</List>
						)}
					</Typography>

					{overtimes.length === 1 && (
						<>
							<Divider sx={{ my: 2 }} />

							<FormControl fullWidth size="small" sx={{ mb: 1 }}>
							<InputLabel>{t("partial.comment.label")}</InputLabel>
							<Select
								value={employeeComments[overtimes[0]?.id] || ""}
								label={t("partial.comment.label")}
								disabled={loadingComments}
								onChange={(e) => setEmployeeComments({ ...employeeComments, [overtimes[0]?.id]: e.target.value })}
							>
								<MenuItem value="">
									<em>{t("partial.comment.placeholder")}</em>
									</MenuItem>
									{comments.map((commentOption) => (
										<MenuItem key={commentOption} value={commentOption}>
											{commentOption}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								fullWidth
								label={t("partial.note.label")}
								placeholder={t("partial.note.placeholder")}
								multiline
								rows={4}
								value={employeeNotes[overtimes[0]?.id] || ""}
								onChange={(e) => setEmployeeNotes({ ...employeeNotes, [overtimes[0]?.id]: e.target.value })}
								size="small"
							/>
						</>
					)}
				</DialogContent>

				<DialogActions>
					<Tooltip
						title={
							data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending").length ===
								0 || overtimes.some((d) => !/^\d{1,2}:[0-5]\d$/.test(d.partial_amount)) ? (
								<List dense={true}>
									{data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending")
										.length === 0 && (
										<ListItem>
											<ListItemText primary={t("partial.buttons.approve.overtime_already_managed")} />
										</ListItem>
									)}

									{overtimes.some((d) => !/^\d{1,2}:[0-5]\d$/.test(d.partial_amount)) && (
										<ListItem>
											<ListItemText primary={t("partial.buttons.approve.format_is_invalid")} />
										</ListItem>
									)}
								</List>
							) : (
								""
							)
						}
					>
						<span>
							<LoadingButton
								variant="contained"
								disableElevation
								onClick={() => {
									trackGaClick({
										id: "overtime.add_approve_overtime",
										gaAction: "approve_overtime",
										gaType: "partial",
										extra: {
											total: overtimes.length,
										},
									});

									const overtimesWithComments = overtimes.map((overtime) => ({
										...overtime,
										comment: employeeComments[overtime.id] || "",
										notes: employeeNotes[overtime.id] || "",
									}));

									applyingOvertime(setLoading, overtimesWithComments, closing, reloading, handlerAlert);
								}}
								color="primary"
								loading={loading}
								disabled={
									data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending").length ===
									0 || overtimes.some((d) => !/^\d{1,2}:[0-5]\d$/.test(d.partial_amount)) ||
								overtimes.some((ot) => employeeComments[ot.id] && !employeeNotes[ot.id]?.trim()) ||
								(overtimeRequiresComment && overtimes.some((ot) => !employeeComments[ot.id] || !employeeNotes[ot.id]?.trim()))
								}
							>
								{t("partial.buttons.approve.title")}
							</LoadingButton>
						</span>
					</Tooltip>

					<Button
						sx={{ mt: 0.5, mr: 2 }}
						disableElevation
						color="error"
						variant="contained"
						onClick={() => {
							closing();
						}}
					>
						{t("partial.buttons.cancel")}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openCommentDialog}
				onClose={() => {
					setOpenCommentDialog(false);
					setSelectedEmployeeId(null);
				}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{t("partial.comment.title")}</DialogTitle>
				<DialogContent>
					<FormControl fullWidth size="small" sx={{ mt: 1 }}>
						<InputLabel>{t("partial.comment.label")}</InputLabel>
						<Select
							value={employeeComments[selectedEmployeeId] || ""}
							label={t("partial.comment.label")}
							disabled={loadingComments}
							onChange={(e) => {
								setEmployeeComments({ ...employeeComments, [selectedEmployeeId]: e.target.value });
							}}
						>
							<MenuItem value="">
								<em>{t("partial.comment.placeholder")}</em>
							</MenuItem>
							{comments.map((commentOption) => (
								<MenuItem key={commentOption} value={commentOption}>
									{commentOption}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpenCommentDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("partial.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							setOpenCommentDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("partial.buttons.save")}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openNoteDialog}
				onClose={() => {
					setOpenNoteDialog(false);
					setSelectedEmployeeId(null);
				}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{t("partial.note.title")}</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						label={t("partial.note.label")}
						placeholder={t("partial.note.placeholder")}
						multiline
						rows={4}
						value={employeeNotes[selectedEmployeeId] || ""}
						onChange={(e) => setEmployeeNotes({ ...employeeNotes, [selectedEmployeeId]: e.target.value })}
						size="small"
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpenNoteDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("partial.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							setOpenNoteDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("partial.buttons.save")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export { Partial };
